import { instance } from "./instance";
import { FileResponse } from "../types/index.d";

const axios = instance("FILE");

// Define a type that matches the actual file API response format
interface FileMetadataResponse {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  mimeType: string;
  altName?: string;
  size?: number;
  metadata?: Record<string, unknown>;
}

// Normalize URL path to avoid duplication
const normalizeUrlPath = (path: string): string => {
  // Remove any leading slashes
  let normalizedPath = path?.startsWith('/') ? path?.substring(1) : path;
  
  // Remove v1 prefix if it exists (we'll add it back in a controlled manner)
  if (normalizedPath?.startsWith('v1/')) {
    normalizedPath = normalizedPath?.substring(3);
  }
  
  return normalizedPath;
};

export const getFile = (fileUrl: string): string => {
  const normalizedPath = normalizeUrlPath(fileUrl);
  return `${import.meta.env.VITE_API_URL}filesvc/v1/${normalizedPath}`;
};

export const getFileId = (fileUrl: string): string => {
  // Extract file ID regardless of URL format
  const parts = fileUrl.split('/');
  // Find the ID (typically a UUID) in the URL path
  for (const part of parts) {
    if (part.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return part;
    }
  }
  return parts[3] || ''; // Fallback to the old method
};

export const getFileMetadata = async (fileId: string): Promise<FileMetadataResponse> => {
  try {
    const response = await axios.get(`/file/${fileId}`);    
    // Check various possible locations of the data
    let fileData;
    if (response.data && typeof response.data === 'object') {
      if (response.data.data) {
        fileData = response.data.data;
      } else {
        fileData = response.data; 
      }
    } else {
      fileData = { AltName: '' };
    }
    
    // Ensure AltName exists
    if (!fileData.AltName && fileData.altName) {
      fileData.AltName = fileData.altName;
    }
    
    return fileData;
  } catch (error) {
    console.error("Error fetching file metadata:", error);
    throw error;
  }
};

export const uploadFile = async (file: File): Promise<FileResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post("/file/upload", formData);
  return response.data;
};